/* eslint-disable*/
import { html, LitElement, render } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import initDatepicker from 'js-datepicker/dist/datepicker.min';
import { LoaderMixin, Mix, dayjs } from '../../common';
import { i18nMixin } from '../../decorators';
import style from './ki-time-range-picker.css?inline';
import { IColumnOption, PeriodsList } from './PeriodsList';
import nls from '../../locales/index';
import { getDefaultJsDatepickerOptions } from './js-datepicker-helper';

import { PeriodListElement } from './period-list';
import { MobileFromToTabs } from './mobile-from-to-tabs';
import {
  getSearchParam,
  setSearchParam,
  hashChangedSubject,
} from '../ki-app/routing/ki-router';
import { Subscription } from 'rxjs/internal/Subscription';

import './mobile-from-to-tabs';
import './period-list';
import { isActivationKey } from '../ki-app';

interface datepicker {
  show: () => void;
  hide: () => void;
  setDate: (date: Date, updateCalendar: boolean) => void;
  navigate: (date: Date) => void;
  getRange: () => {
    start: Date;
    end: Date;
  };
  calendarContainer: HTMLElement;
}

@customElement('ki-time-range-picker')
export class KiTimeRangePicker extends Mix(
  LitElement,
  [i18nMixin, { nls }],
  LoaderMixin,
) {
  // language=CSS
  static styles = style;

  @property({ type: Boolean })
  allowtimebox = false;

  @property({ type: Object })
  constraints = {};

  fromDate: datepicker | null = null;

  toDate: datepicker | null = null;

  @property({ type: Boolean })
  showPeriodList: boolean = false;

  _periodsListOptions: Array<Array<IColumnOption>> | null = null;

  @property({ type: Array })
  periods = [];

  @property({ type: String })
  dateFormat = 'DD/MM/YYYY';

  @property({ type: Number })
  initMin: number | null = null;

  @property({ type: Number })
  initMax: number | null = null;

  @property({ type: String, attribute: false })
  selectedPeriod = '';

  @property({ type: String, attribute: false })
  rangeSummaryValue = '';

  @property({ type: Boolean, attribute: false })
  datepickerOpen = false;

  _mobileFromToTabs: Array<MobileFromToTabs> = [];
  _periodLists: Array<PeriodListElement> = [];

  subscription: Subscription | undefined;

  _createInputs() {
    const _t = this;
    const id = dayjs().toISOString();

    const options = getDefaultJsDatepickerOptions(
      id,
      this.i18n,
      (input, date) => {
        input.value = dayjs(date).format(_t.dateFormat);
      },
      () => {
        _t.datepickerOpen = false;
      },
    );

    const fromOptions = {
      ...options,
      onShow: () => {
        this._mobileFromToTabs.forEach(
          mobileTab => (mobileTab.selectedTab = 'from'),
        );
        _t.datepickerOpen = true;
      },
      onSelect: (instance: datepicker) => {
        _t.toDate?.show();
        _t._checkRangeAndEmitChanges(instance);
      },
    };

    const toOptions = {
      ...options,
      onShow: () => {
        this._mobileFromToTabs.forEach(
          mobileTab => (mobileTab.selectedTab = 'to'),
        );
        _t.datepickerOpen = true;
      },
      onSelect: (instance: datepicker) => {
        _t._checkRangeAndEmitChanges(instance);
      },
    };

    this.fromDate = initDatepicker(
      this.renderRoot.querySelector('#from'),
      fromOptions,
    );
    this.toDate = initDatepicker(
      this.renderRoot.querySelector('#to'),
      toOptions,
    );

    // Add keyboard event listeners to the input fields
    this._addInputKeyboardHandlers();
  }

  _addInputKeyboardHandlers() {
    const fromInput = this.renderRoot.querySelector(
      '#from',
    ) as HTMLInputElement;
    const toInput = this.renderRoot.querySelector('#to') as HTMLInputElement;

    if (fromInput) {
      fromInput.addEventListener('keydown', (event: KeyboardEvent) => {
        this._handleInputKeyDown(event, 'from');
      });

      fromInput.addEventListener('focus', () => {
        // Auto-show calendar when input is focused
        setTimeout(() => {
          this.toDate?.hide();
          this.fromDate?.show();
          this.datepickerOpen = true;
        }, 100);
      });

      // Handle input changes (when user types manually)
      fromInput.addEventListener('input', (event: Event) => {
        this._handleManualDateInput(event.target as HTMLInputElement, 'from');
      });

      fromInput.addEventListener('blur', (event: Event) => {
        this._handleInputBlur(event.target as HTMLInputElement, 'from');
      });
    }

    if (toInput) {
      toInput.addEventListener('keydown', (event: KeyboardEvent) => {
        this._handleInputKeyDown(event, 'to');
      });

      toInput.addEventListener('focus', () => {
        // Auto-show calendar when input is focused
        setTimeout(() => {
          this.fromDate?.hide();
          this.toDate?.show();
          this.datepickerOpen = true;
        }, 100);
      });

      // Handle input changes (when user types manually)
      toInput.addEventListener('input', (event: Event) => {
        this._handleManualDateInput(event.target as HTMLInputElement, 'to');
      });

      toInput.addEventListener('blur', (event: Event) => {
        this._handleInputBlur(event.target as HTMLInputElement, 'to');
      });
    }
  }

  _handleManualDateInput(input: HTMLInputElement, inputType: 'from' | 'to') {
    const inputValue = input.value;

    // Try to parse the date as user types
    if (inputValue.length >= 8) {
      // Minimum length for a date
      const parsedDate = dayjs(inputValue, this.dateFormat);

      if (parsedDate.isValid()) {
        // Update the datepicker with the new date
        if (inputType === 'from' && this.fromDate) {
          this.fromDate.setDate(parsedDate.toDate(), true);
          this.fromDate.navigate(parsedDate.toDate());
        } else if (inputType === 'to' && this.toDate) {
          this.toDate.setDate(parsedDate.toDate(), true);
          this.toDate.navigate(parsedDate.toDate());
        }
      }
    }
  }

  _handleInputBlur(input: HTMLInputElement, inputType: 'from' | 'to') {
    const inputValue = input.value;

    if (inputValue) {
      const parsedDate = dayjs(inputValue, this.dateFormat);

      if (parsedDate.isValid()) {
        // Update the datepicker and trigger change event
        if (inputType === 'from' && this.fromDate) {
          this.fromDate.setDate(parsedDate.toDate(), true);
        } else if (inputType === 'to' && this.toDate) {
          this.toDate.setDate(parsedDate.toDate(), true);
        }

        // Check if we have both dates and emit change
        this._checkRangeAndEmitChanges(
          inputType === 'from' ? this.fromDate : this.toDate,
        );
      } else {
        console.log('Invalid date format, resetting input');
        // Reset to previous valid value or clear
        if (inputType === 'from' && this.fromDate) {
          const currentDate = this.fromDate.getRange?.()?.start;
          if (currentDate) {
            input.value = dayjs(currentDate).format(this.dateFormat);
          }
        } else if (inputType === 'to' && this.toDate) {
          const currentDate = this.toDate.getRange?.()?.end;
          if (currentDate) {
            input.value = dayjs(currentDate).format(this.dateFormat);
          }
        }
      }
    }
  }

  _handleInputKeyDown(event: KeyboardEvent, inputType: 'from' | 'to') {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();

      if (inputType === 'from') {
        this.toDate?.hide();
        setTimeout(() => {
          this.fromDate?.show();
          this.datepickerOpen = true;
        }, 10);
      } else {
        this.fromDate?.hide();
        setTimeout(() => {
          this.toDate?.show();
          this.datepickerOpen = true;
        }, 10);
      }
    }

    if (event.key === 'Tab' && !event.shiftKey && inputType === 'from') {
      // Don't prevent default - let tab work normally
      setTimeout(() => {
        this.fromDate?.hide();
        this.toDate?.show();
        this.datepickerOpen = true;
      }, 50);
    }

    if (event.key === 'Tab' && event.shiftKey && inputType === 'to') {
      setTimeout(() => {
        this.toDate?.hide();
        this.fromDate?.show();
        this.datepickerOpen = true;
      }, 50);
    }

    if (event.key === 'Escape') {
      this.fromDate?.hide();
      this.toDate?.hide();
      this.datepickerOpen = false;
    }

    // Handle direct date input - when user types a date manually
    if (event.key === 'Enter' && (event.target as HTMLInputElement).value) {
      const inputValue = (event.target as HTMLInputElement).value;
      const parsedDate = dayjs(inputValue, this.dateFormat);

      if (parsedDate.isValid()) {
        if (inputType === 'from') {
          this.fromDate?.setDate(parsedDate.toDate(), true);
        } else {
          this.toDate?.setDate(parsedDate.toDate(), true);
        }

        // Trigger the change detection
        this._checkRangeAndEmitChanges(
          inputType === 'from' ? this.fromDate : this.toDate,
        );
      } else {
        console.log('Invalid date format');
      }
    }
  }

  _generatePeriodList(node: HTMLElement) {
    const periodList = html`
      <ww-period-list
        class="period-list"
        .options=${this._periodsListOptions}
        .selectedPeriod=${this.selectedPeriod}
        .constraints=${this.constraints}
        @period-selected=${(e: {
          detail: {
            value: string;
            from: number;
            to: number;
          };
        }) => {
          this._selectPeriod(e.detail.value);
          this.selectPeriod(e.detail.from, e.detail.to, true);
        }}
      ></ww-period-list>
    `;

    render(periodList, node, {
      renderBefore: node.childNodes[0],
    });

    const foundPeriodList = Array.from(node.childNodes.values()).find(
      child => child.nodeName === 'WW-PERIOD-LIST',
    );

    if (foundPeriodList) {
      this._periodLists.push(foundPeriodList as PeriodListElement);
    }
  }

  _createTabsForMobile(node: HTMLElement) {
    const mobileTabs = html`<ww-mobile-tabs
      class="mobile-tabs"
      @from-clicked=${() => {
        this.toDate?.hide();
        this.fromDate?.show();
      }}
      @to-clicked=${() => {
        this.fromDate?.hide();
        this.toDate?.show();
      }}
    ></ww-mobile-tabs>`;

    render(mobileTabs, node, {
      renderBefore: node.childNodes[0],
    });

    const foundMobileTab = Array.from(node.childNodes.values()).find(
      child => child.nodeName === 'WW-MOBILE-TABS',
    );

    if (foundMobileTab) {
      this._mobileFromToTabs.push(foundMobileTab as MobileFromToTabs);
    }
  }

  renderPeriodLists() {
    const _t = this;
    const periodsList = new PeriodsList({
      allowtime: this.allowtimebox,
      periods: this.periods,
    });
    this._periodsListOptions = periodsList.getPeriodsList();
    const nodes = this.renderRoot.querySelectorAll('.qs-datepicker-container');
    nodes.forEach((node: HTMLElement) => {
      _t._generatePeriodList(node);
      _t._createTabsForMobile(node);
    });
  }

  firstUpdated() {
    this._createInputs();
    this.renderPeriodLists();
    this._setFirstDates();

    // Add a small delay to ensure datepicker is fully initialized
    setTimeout(() => {
      this._addInputKeyboardHandlers();
    }, 100);

    // detect url change
    this.subscription = hashChangedSubject.subscribe(() => {
      const tsDateParam = getSearchParam('tsRangeFromTo');
      if (tsDateParam) {
        const dateRange = tsDateParam?.split(',');
        const from = dateRange[0];
        const to = dateRange[1];
        this.selectPeriod(Number(from), Number(to), true);
      }
    });
  }

  disconnectedCallback() {
    this.subscription?.unsubscribe();
  }

  setMobileTabValues(from: number | null, to: number | null) {
    const fromValue = from !== null ? dayjs(from).format(this.dateFormat) : '';
    const toValue = to !== null ? dayjs(to).format(this.dateFormat) : '';

    this.rangeSummaryValue = `${fromValue} - ${toValue}`;

    this._mobileFromToTabs.forEach(mobileTab => {
      mobileTab.fromValue = fromValue;
      mobileTab.toValue = toValue;
    });
  }

  selectPeriod(from: number, to: number, emitChange: boolean) {
    this.fromDate?.setDate(new Date(from), false);
    this.toDate?.setDate(new Date(to), false);

    this._updateSelections(from, to);

    emitChange && this._emitDatesChanged(from, to);
  }

  _emitDatesChanged(from: number, to: number, ignoreHistory: boolean = false) {
    const auxFrom = dayjs(from).startOf('day');
    const auxTo = dayjs(to).endOf('day');
    this.dispatchEvent(
      new CustomEvent('changedate', {
        bubbles: true,
        detail: {
          fromTime: auxFrom,
          toTime: auxTo,
          ignoreHistory: ignoreHistory,
          fromTimeUnix: dayjs(auxFrom).valueOf(),
          toTimeUnix: dayjs(auxTo).valueOf(),
        },
      }),
    );
  }

  _updateSelections(from: number, to: number) {
    this._deselectPeriod();
    this.toDate?.navigate(new Date(to));
    this.fromDate?.navigate(new Date(from));
    this.setMobileTabValues(from, to);
    this._checkPeriodListSelection(from, to);
  }

  _selectPeriod(period: string) {
    this.selectedPeriod = period;

    this._periodLists.forEach(periodList => {
      periodList.selectedPeriod = this.selectedPeriod;
    });
  }

  _deselectPeriod() {
    this._selectPeriod('');
  }

  _checkPeriodListSelection(from: number, to: number) {
    const ONE_DAY_MS = 86400000;
    const isPeriodLess24H = to - from < ONE_DAY_MS;
    this._deselectPeriod();

    this._periodsListOptions?.forEach(column => {
      column.forEach(period => {
        const _periodStart = period.dates.from;
        const _periodEnd = period.dates.to;

        const _firstCheck = _periodStart === from && _periodEnd === to;

        const _additionalCheck = isPeriodLess24H
          ? false
          : dayjs(_periodStart).startOf('day').valueOf() === from &&
            dayjs(_periodEnd).endOf('day').valueOf() === to;

        if (_firstCheck || _additionalCheck) {
          this._selectPeriod(`from_${period.dates.from}_to_${period.dates.to}`);
        }
      });
    });
  }

  _setFirstDates() {
    if (!this.initMin || !this.initMax) {
      return;
    }
    setSearchParam('tsRangeFromTo', [this.initMin, this.initMax].join(','));
    this.selectPeriod(this.initMin, this.initMax, false);
  }

  _handleKeyDown(event: KeyboardEvent) {
    if (
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'Spacebar' ||
      isActivationKey(event)
    ) {
      event.preventDefault();
      event.stopPropagation();
      this.openFromCalendar(event);
    }
  }

  // Alternative keyboard handler that bypasses the icon button
  _handleDirectKeyDown(event: KeyboardEvent) {
    if (
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'Spacebar'
    ) {
      event.preventDefault();
      event.stopPropagation();

      // Directly call the calendar opening logic
      this.toDate?.hide();
      if (this.fromDate) {
        const isHidden =
          this.fromDate?.calendarContainer.classList.contains('qs-hidden');
        this.fromDate[isHidden ? 'show' : 'hide']();
      }
    }
  }

  render() {
    // language=html
    return html`
      <div class="input-group">
        <input
          id="from"
          type="text"
          aria-label="Start date"
          aria-haspopup="dialog"
          aria-expanded="${this.datepickerOpen}"
        />
        <input
          id="to"
          type="text"
          aria-label="End date"
          aria-haspopup="dialog"
          aria-expanded="${this.datepickerOpen}"
        />
        <ki-icon-btn
          tabindex="0"
          id="periodListBtn"
          class="${this.showPeriodList ? '' : 'display-none'}${this
            .datepickerOpen
            ? ' selected'
            : ''}"
          title="${this.i18n.t('selectTimePeriod')}"
          icon="ki ki-calendar"
          @click="${this.openFromCalendar}"
          @keydown="${this._handleKeyDown}"
          @keyup="${this._handleDirectKeyDown}"
          aria-label="Open calendar"
          aria-haspopup="dialog"
          aria-expanded="${this.datepickerOpen}"
        ></ki-icon-btn>

        <!-- Fallback button for keyboard users -->
        <button
          class="sr-only keyboard-fallback"
          @click="${this.openFromCalendar}"
          @keydown="${this._handleDirectKeyDown}"
          aria-label="Open calendar (keyboard accessible)"
        >
          ${this.i18n.t('openCalendar') ?? "Open calendar"}
        </button>
      </div>
      <div
        id="periodListSummary"
        class="${this.datepickerOpen ? 'selected' : ''}"
        tabindex="0"
        role="button"
        @click="${this.openFromCalendar}"
        @keydown="${this._handleDirectKeyDown}"
        aria-live="polite"
        aria-label="Date range selection"
      >
        ${this.rangeSummaryValue}
        <ki-icon icon="ki ki-calendar"></ki-icon>
      </div>
    `;
  }

  _checkRangeAndEmitChanges(input: datepicker | null) {
    if (input === null) {
      return;
    }
    const range = input.getRange();
    const from = range.start.getTime();
    const to = range.end.getTime();
    this.setMobileTabValues(from, to);
    if (range && range.start && range.end) {
      this._deselectPeriod();
      this._emitDatesChanged(from, to);
    }
    setSearchParam('tsRangeFromTo', [from, to].join(','));
  }

  openFromCalendar(e: Event) {
    e.stopPropagation();
    this.toDate?.hide();
    if (this.fromDate) {
      const isHidden =
        this.fromDate?.calendarContainer.classList.contains('qs-hidden');
      this.fromDate[isHidden ? 'show' : 'hide']();
    }
  }
}
